import React, { useState } from "react";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Play, Sparkles } from "lucide-react";

const YOUTUBE_EMBED_URL =
    "https://www.youtube.com/embed/Oj2vG-lH6oU?rel=0&modestbranding=1";

const VideoSection = () => {
    const [showVideo, setShowVideo] = useState(false);

    return (
        <section
            id="demo-video"
            className="py-24 bg-slate-900 relative overflow-hidden"
        >
            {/* Background decoration */}
            <div className="absolute inset-0 opacity-10">
                <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-500 rounded-full filter blur-3xl"></div>
                <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-teal-500 rounded-full filter blur-3xl"></div>
            </div>

            <div className="relative max-w-6xl mx-auto px-6">
                {/* Section header */}
                <div className="text-center mb-12 max-w-3xl mx-auto">
                    <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-900/30 border border-blue-700 rounded-full mb-6">
                        <Sparkles className="w-4 h-4 text-blue-400" />
                        <span className="text-sm font-medium text-blue-300">
                            See It In Action
                        </span>
                    </div>
                    <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6">
                        Watch Nucleus Transform
                        <br />
                        <span className="bg-gradient-to-r from-blue-400 to-teal-400 bg-clip-text text-transparent">
                            Engineering Operations
                        </span>
                    </h2>
                    <p className="text-xl text-slate-300 leading-relaxed">
                        See how engineering leaders at top companies use Nucleus
                        to save 6+ hours every week and make better, faster
                        decisions.
                    </p>
                </div>

                {/* Video player */}
                <Card className="relative aspect-video bg-slate-800 border-2 border-slate-700 overflow-hidden">
                    {showVideo ? (
                        <iframe
                            src={`${YOUTUBE_EMBED_URL}&autoplay=1`}
                            title="Nucleus product demo video"
                            className="w-full h-full"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                            referrerPolicy="strict-origin-when-cross-origin"
                            allowFullScreen
                        />
                    ) : (
                        <button
                            type="button"
                            onClick={() => setShowVideo(true)}
                            className="relative w-full h-full text-left"
                            aria-label="Play demo video"
                        >
                            <img
                                src="/seo_card.png"
                                alt="Nucleus Demo Thumbnail"
                                className="w-full h-full object-cover"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 via-slate-900/20 to-transparent" />
                            <div className="absolute inset-0 flex items-center justify-center">
                                <Button
                                    type="button"
                                    size="lg"
                                    className="w-24 h-24 rounded-full bg-white hover:bg-blue-50 text-blue-600 shadow-2xl pointer-events-none"
                                >
                                    <Play className="w-12 h-12 fill-current" />
                                </Button>
                            </div>
                        </button>
                    )}
                </Card>

                {/* Video features */}
                <div className="grid md:grid-cols-3 gap-6 mt-12">
                    <div className="text-center">
                        <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center mx-auto mb-4">
                            <span className="text-2xl font-bold text-white">
                                5
                            </span>
                        </div>
                        <h4 className="text-white font-semibold mb-2">
                            Minutes
                        </h4>
                        <p className="text-slate-400 text-sm">
                            Complete product overview
                        </p>
                    </div>
                    <div className="text-center">
                        <div className="w-12 h-12 bg-teal-600 rounded-lg flex items-center justify-center mx-auto mb-4">
                            <span className="text-2xl font-bold text-white">
                                10+
                            </span>
                        </div>
                        <h4 className="text-white font-semibold mb-2">
                            Use Cases
                        </h4>
                        <p className="text-slate-400 text-sm">
                            Real engineering scenarios
                        </p>
                    </div>
                    <div className="text-center">
                        <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center mx-auto mb-4">
                            <span className="text-2xl font-bold text-white">
                                6
                            </span>
                        </div>
                        <h4 className="text-white font-semibold mb-2">
                            Integrations
                        </h4>
                        <p className="text-slate-400 text-sm">
                            Setup walkthrough included
                        </p>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default VideoSection;
